import {
  Box,
  Button,
  FormControlLabel,
  Link,
  Spinner,
  Typography,
} from '@forgeglobal/design-framework';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import { MaritalStatus } from '@tcs/client-portal-api/types/Beneficiaries';
import { Beneficiary } from '@tcs/client-portal-api/types/Beneficiary';
import useProfile from '@tcs/client-portal-common/hooks/useProfile';
import { MARITAL_STATUS } from '@tcs/common/assets/constants';
import Checkbox from '@tcs/common/components/atoms/Checkbox';
import DropdownField from '@tcs/common/components/atoms/DropdownField';
import Form, {
  Field,
  FormRenderProps,
  FormSpy,
  useFormState,
} from '@tcs/common/components/atoms/Form';
import arrayMutators from 'final-form-arrays';
import { useEffect, useState } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import BeneficiariesFieldArray from '../BeneficiariesFieldArray';
import useStyles from '../style';
import {
  beneficiariesListSchema,
  useBeneficiariesErrors,
} from './BeneficiariesValidators';
import { useForm } from 'react-final-form';

const BeneficiariesList = ({
  accountNumber,
  handleSubmit,
  handleClose,
  setShowExitPrompt,
}) => {
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const classes = useStyles();
  const { hasErrors, latestError, isCommunityPropertyState } =
    useBeneficiariesErrors(accountNumber);
  const { submitting } = useFormState();
  const [showMaritalStatusDialog, setShowMaritalStatusDialog] = useState(false);

  const onClickConfirm = () => {
    const values = form.getState().values;
    if (!values.maritalStatus && isCommunityPropertyState) {
      setShowMaritalStatusDialog(true);
      return;
    }

    if (hasErrors) {
      setShowErrorDialog(true);
    }
  };

  const form = useForm();
  const values = form.getState().values;
  if (
    !values.maritalStatus &&
    (values.primaryBeneficiaries?.length > 0 ||
      values.contingentBeneficiaries?.length > 0)
  ) {
    form.blur('maritalStatus'); // Mark the field as touched
  }

  return (
    <>
      <form onSubmit={handleSubmit} data-test-id="beneficiaries-form">
        <FormSpy
          subscription={{ dirty: true }}
          onChange={({ dirty }) => {
            if (dirty) {
              setShowExitPrompt(true);
            }
          }}
        />
        <div className={classes.wrapper}>
          <Typography
            variant="h5"
            className={classes.title}
            data-test-id="beneficiaries-title">
            Beneficiaries
          </Typography>
          <Typography
            data-test-id="accountNumber"
            variant="h6"
            className={classes.accountNumber}>
            Account Number: {accountNumber}
          </Typography>
          {isCommunityPropertyState && (
            <Box sx={{ pb: 3, '& .MuiTypography-root': { mb: 0 } }}>
              <Typography variant="body2" className={classes.subTitle}>
                What is your marital status?{' '}
                <Link
                  onClick={() => setShowInfoDialog(true)}
                  sx={{ cursor: 'pointer' }}>
                  Why do we ask for this information?
                </Link>
              </Typography>
              <Box
                sx={{ width: '30%', pt: 1 }}
                data-test-id="beneficiaries-form-marital-status">
                <Field
                  name="maritalStatus"
                  render={(props) => (
                    <DropdownField
                      label="Marital Status"
                      required
                      {...props}
                      options={MARITAL_STATUS}
                    />
                  )}
                />
              </Box>
            </Box>
          )}
          <Box data-test-id="beneficiaries-form-primary-beneficiaries">
            <Typography
              variant="h6"
              className={classes.tableTitle}
              data-test-id="primary-beneficiaries-title">
              Primary Beneficiaries
            </Typography>
            {!isCommunityPropertyState && (
              <Typography variant="body2" className={classes.subTitle}>
                You can add up to 9 primary beneficiaries.
              </Typography>
            )}
            {isCommunityPropertyState && (
              <Typography variant="body2" className={classes.subTitle}>
                Our records indicate that you reside in a community property
                state. By default, you must designate your spouse as the sole
                primary beneficiary of your account. If you wish to designate
                someone else as the primary beneficiary of your account, click{' '}
                <Link
                  onClick={() => setShowInfoDialog(true)}
                  sx={{ cursor: 'pointer' }}>
                  here
                </Link>{' '}
                to learn more.
              </Typography>
            )}
            <FieldArray name="primaryBeneficiaries">
              {(
                fieldArrayProps: FieldArrayRenderProps<
                  Beneficiary,
                  HTMLInputElement
                >,
              ) => (
                <BeneficiariesFieldArray
                  {...fieldArrayProps}
                  accountNumber={accountNumber}
                  isPrimary={true}
                />
              )}
            </FieldArray>
          </Box>
          <Box
            className={classes.contingentTable}
            data-test-id="beneficiaries-form-contingent-beneficiaries">
            <Typography
              variant="h6"
              className={classes.tableTitle}
              data-test-id="contingent-beneficiaries-title">
              Contingent Beneficiaries
            </Typography>
            <Typography variant="body2" className={classes.subTitle}>
              You must designate a primary beneficiary before adding contingent
              beneficiaries. You can add up to 9 contingent beneficiaries.
            </Typography>
            <FieldArray name="contingentBeneficiaries">
              {(
                fieldArrayProps: FieldArrayRenderProps<
                  Beneficiary,
                  HTMLInputElement
                >,
              ) => (
                <BeneficiariesFieldArray
                  {...fieldArrayProps}
                  accountNumber={accountNumber}
                  isPrimary={false}
                />
              )}
            </FieldArray>
          </Box>
          <Field
            name="applyToChildAccounts"
            type="checkbox"
            render={(props) => (
              <FormControlLabel
                control={<Checkbox {...props} />}
                label={
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Apply this beneficiary designation to all accounts I own
                    with Forge Trust.
                  </Typography>
                }
                sx={{ m: 0, pt: 2 }}
              />
            )}
          />
          <div className={classes.buttonContainer}>
            <Button
              data-test-id="exit-button"
              size="medium"
              color="secondary"
              onClick={() => handleClose(false)}
              disabled={submitting}>
              Exit
            </Button>
            <Button
              color="primary"
              size="medium"
              className={classes.primaryButton}
              type="submit"
              data-test-id="submit-button"
              onClick={onClickConfirm}
              disabled={submitting}>
              Save
            </Button>
          </div>
        </div>
      </form>
      <Dialog open={submitting} maxWidth="xs">
        <DialogContent>
          <Spinner size="small" />
        </DialogContent>
      </Dialog>
      <Dialog open={showErrorDialog} maxWidth="xs" fullWidth>
        <DialogContent className={classes.dialogContent}>
          <DialogContentText>{latestError}</DialogContentText>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setShowErrorDialog(false)}>Ok</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showInfoDialog} maxWidth="sm" fullWidth>
        <DialogContent className={classes.dialogContent}>
          <Typography variant="body1" sx={{ pb: 2 }}>
            Our records indicate that you reside in a community property state.
            By default, your spouse must be named as the sole primary
            beneficiary of your assets.
          </Typography>
          <Typography variant="body1" sx={{ pb: 2 }}>
            To name someone else as a primary beneficiary of your account, you
            must complete, notarize, and return the form below
          </Typography>
          <Link
            href="https://forgetrust.com/forms/beneficiary-designation-or-changes/"
            target="_new">
            Beneficiary Designation
          </Link>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setShowInfoDialog(false)}>Ok</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showMaritalStatusDialog} maxWidth="sm" fullWidth>
        <DialogContent className={classes.dialogContent}>
          <DialogContentText>
            Please enter your marital status.
          </DialogContentText>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setShowMaritalStatusDialog(false)}>Ok</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface BeneficiariesListFormProps {
  accountNumber: string;
  onSubmit: (data: FormData) => Promise<void>;
  onCancel: () => void;
  primaryBeneficiaries: Beneficiary[];
  contingentBeneficiaries: Beneficiary[];
  maritalStatus: MaritalStatus;
  setShowExitPrompt: (showExitPrompt: boolean) => void;
}

const BeneficiariesListForm = ({
  accountNumber,
  onSubmit,
  onCancel,
  primaryBeneficiaries,
  contingentBeneficiaries,
  maritalStatus,
  setShowExitPrompt,
}: BeneficiariesListFormProps) => {
  const { data: profile } = useProfile(accountNumber);

  useEffect(() => {
    return () => {
      setShowExitPrompt(false);
    };
  }, []);

  return (
    <Form
      onSubmit={onSubmit}
      mutators={{
        ...arrayMutators,
      }}
      initialValues={{
        primaryBeneficiaries,
        contingentBeneficiaries,
        maritalStatus: maritalStatus,
        applyToChildAccounts: false,
      }}
      validationSchema={beneficiariesListSchema(profile)}
      render={({ handleSubmit }: FormRenderProps<FormData>) => (
        <BeneficiariesList
          accountNumber={accountNumber}
          handleSubmit={handleSubmit}
          handleClose={onCancel}
          setShowExitPrompt={setShowExitPrompt}
        />
      )}
    />
  );
};

export default BeneficiariesListForm;
